import { useCallback, useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useParams as useRouterParams,
  useSearchParams as useRouterSearchParams,
} from "react-router";

import type {
  NavigationAdapter,
  SearchParamHistory,
  SearchParamsSetter,
  SearchParamsTuple,
  SearchParamTuple,
} from "./types";

/**
 * Whether a page of this app is behind the current one. React Router's browser
 * routers keep their entry index in `history.state.idx`: 0 is the first page of
 * this visit (opened by a link or in a new tab), even after a redirect replaces it.
 */
export function hasAppHistory(): boolean {
  const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
  return idx > 0;
}

/** Web navigation adapter — wraps React Router 7. */
export function useNavigation(): NavigationAdapter {
  const navigate = useNavigate();
  // Memoize so callers can safely list these in effect deps; `navigate` is
  // stable across renders, so an unstable object here would churn deps and,
  // for self-redirects, loop `history.replaceState` into a Firefox SecurityError.
  return useMemo(
    () => ({
      push: (path) => void navigate(path),
      replace: (path) => void navigate(path, { replace: true }),
      goBack: (fallback = "/") => {
        if (hasAppHistory()) void navigate(-1);
        else void navigate(fallback, { replace: true });
      },
      reset: (path) => void navigate(path, { replace: true }),
    }),
    [navigate],
  );
}

/** Current URL pathname (no search / hash). */
export function usePathname(): string {
  return useLocation().pathname;
}

/**
 * Returns route params merged with search params. Route params win on key
 * collision. Mirrors Expo Router's `useLocalSearchParams` semantics so the
 * shape is identical across platforms.
 */
export function useParams<
  T extends string | Record<string, string | string[] | undefined> = Record<
    string,
    string | string[] | undefined
  >,
>(): Readonly<Partial<T>> {
  const routeParams = useRouterParams();
  const { search } = useLocation();
  return useMemo(() => {
    const merged: Record<string, string | undefined> = { ...routeParams };
    const params = new URLSearchParams(search);
    params.forEach((value, key) => {
      if (merged[key] === undefined) merged[key] = value;
    });
    return merged as Readonly<Partial<T>>;
  }, [routeParams, search]);
}

const HIDDEN_FOLDER_RE = /^\(.*\)$/;

/**
 * Pathname split into segments. `skipHiddenFolder` matches Expo Router's
 * `useSegments` behavior — on web, URL paths never contain `(group)` syntax
 * so the option is a no-op, kept for cross-platform parity.
 */
export function useSegments(skipHiddenFolder = true): string[] {
  const { pathname } = useLocation();
  const all = pathname.split("/").filter(Boolean);
  return skipHiddenFolder ? all.filter((s) => !HIDDEN_FOLDER_RE.test(s)) : all;
}

/**
 * Full `URLSearchParams` plus a setter. Use this when you need to read or
 * manipulate multiple keys at once; for a single key prefer {@link useSearchParam}.
 */
export function useSearchParams(): SearchParamsTuple {
  const [params, setParams] = useRouterSearchParams();
  const setter: SearchParamsSetter = useCallback(
    (next, opts) => {
      setParams(next, { replace: opts?.replace ?? true });
    },
    [setParams],
  );
  return [params, setter];
}

/**
 * Reads and writes a single URL search param. `opts.history` controls whether
 * a write pushes (default: `"replace"`, so filter changes don't pile up).
 */
export function useSearchParam<T extends string = string>(
  key: string,
  opts?: { history?: SearchParamHistory },
): SearchParamTuple<T> {
  const [searchParams, setSearchParams] = useRouterSearchParams();
  const value = (searchParams.get(key) as T | null) ?? null;

  const setValue = useCallback(
    (next: T | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === null) {
            params.delete(key);
          } else {
            params.set(key, next);
          }
          return params;
        },
        { replace: opts?.history !== "push" },
      );
    },
    [key, opts?.history, setSearchParams],
  );

  return [value, setValue];
}
